<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>Gateway</label>
                <select
                  class="form-control form-control-sm"
                  v-model="gateway"
                  @change="loadReport"
                >
                  <option value="remita">Remita</option>
                  <option value="interswitch">Interswitch</option>
                </select>
              </div>
            </div>
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>Transaction Source</label>
                <select
                  class="form-control form-control-sm"
                  v-model="type"
                  @change="loadReport"
                >
                  <option value="applicant">Applicant</option>
                  <option value="student">Student</option>
                </select>
              </div>
            </div>
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>Session</label>
                <select
                  class="form-control form-control-sm"
                  v-model="session"
                  @change="loadReport"
                >
                  <option value="">Current Session</option>
                  <option v-for="(s, index) in sessionOptions" :key="index">
                    {{ s }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>Status</label>
                <select
                  class="form-control form-control-sm"
                  v-model="status"
                  @change="loadReport"
                >
                  <option value="">All</option>
                  <option value="SUCCESS">Success</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>Start Date</label>
                <b-form-datepicker
                  v-model="startDate"
                  size="sm"
                  @input="loadReport"
                ></b-form-datepicker>
              </div>
            </div>
            <div class="col">
              <div class="form-group m-0 p-0">
                <label>End Date</label>
                <b-form-datepicker
                  v-model="endDate"
                  size="sm"
                  @input="loadReport"
                ></b-form-datepicker>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between mb-2 align-items-center">
        <div>
          <small class="text-muted" v-if="reportSession">
            Session: <strong>{{ reportSession }}</strong> |
            Gateway: <strong>{{ gateway === 'interswitch' ? 'Interswitch' : 'Remita' }}</strong> |
            Source: <strong>{{ type === 'applicant' ? 'Applicant' : 'Student' }}</strong>
          </small>
        </div>
        <button class="btn btn-info btn-sm" @click="downloadReport" :disabled="downloadBusy">
          <b-spinner small v-if="downloadBusy"></b-spinner>
          <i v-else class="fas fa-download"></i> Download CSV
        </button>
      </div>

      <b-table
        :busy="isBusy"
        :items="items"
        :fields="fields"
        :filter="filter"
        :current-page="currentPage"
        :per-page="perPage"
        stacked="md"
        show-empty
        small
        hover
        @filtered="onFiltered"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template #cell(amount)="{ value }">
          {{ formatCurrency(value) }}
        </template>

        <template #cell(status)="{ value }">
          <b-badge :variant="value === 'SUCCESS' ? 'success' : 'warning'">
            {{ value }}
          </b-badge>
        </template>

        <template #cell(gateway)="{ value }">
          {{ value === 'interswitch' ? 'Interswitch' : 'Remita' }}
        </template>

        <template #cell(reference)="row">
          {{ row.item.rrr || row.item.gateway_ref || '' }}
        </template>

        <template #cell(updated_at)="{ value }">
          {{ formatDate(value) }}
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="my-0"
          ></b-pagination>
        </b-col>
        <b-col md="6" class="my-1 text-right">
          <small class="text-muted">{{ totalRows }} record(s)</small>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapState } from "vuex";
function pad(n) {
  return n < 10 ? "0" + n : n;
}
export default {
  data() {
    return {
      isBusy: false,
      downloadBusy: false,
      gateway: "remita",
      type: "applicant",
      session: "",
      status: "",
      startDate: "",
      endDate: "",
      reportSession: "",
      items: [],
      filter: null,
      currentPage: 1,
      perPage: 50,
      totalRows: 0,
      fields: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Name", sortable: true },
        { key: "email", label: "Email", sortable: true },
        { key: "payment_type", label: "Payment Type", sortable: true },
        { key: "degree", label: "Degree", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "reference", label: "Reference" },
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "updated_at", label: "Date", sortable: true },
      ],
    };
  },
  computed: {
    ...mapState({ settings: (state) => state.settings }),
    sessionOptions() {
      if (!this.settings || !this.settings.length) return [];
      return [...new Set(this.settings.map((s) => s.session_name))];
    },
  },
  created() {
    this.$store.dispatch("getSettings");
    this.loadReport();
  },
  methods: {
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    buildParams() {
      let params = {
        gateway: this.gateway,
        type: this.type,
      };
      if (this.session) params.session = this.session;
      if (this.status) params.status = this.status;
      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate) params.end_date = this.endDate;
      return params;
    },
    loadReport() {
      this.isBusy = true;
      this.currentPage = 1;

      axios
        .get("admin/bursary/gateway-report", { params: this.buildParams() })
        .then((response) => {
          this.reportSession = response.data.session || "";
          const paginated = response.data.data || {};
          this.items = paginated.data || [];
          this.totalRows = paginated.total || this.items.length;
          this.perPage = paginated.per_page || 50;
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load gateway report" });
          this.items = [];
          this.totalRows = 0;
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    downloadReport() {
      this.downloadBusy = true;

      axios({
        url: "admin/bursary/gateway-report/download",
        method: "GET",
        params: this.buildParams(),
        responseType: "blob",
      })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${this.gateway}_${this.type}_report.csv`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Download failed" });
        })
        .finally(() => {
          this.downloadBusy = false;
        });
    },
  },
};
</script>
